import React, { useEffect, useState } from 'react';
import { Input, Modal, PhoneInput, TextArea, useForm } from 'linkwithus';
import './styles.scss';
import { motion } from 'framer-motion';
import config from 'config';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import SiteButton from 'components/SiteButton';
import FileUploader from 'components/FileUploader';
import { phoneRegex } from 'utils/regex';
import {
    GastronomyModalMode,
    IGastronomyForm,
    IGastronomyModalProps,
} from './interface';
import { contentSettingsModalMotion } from './animation';

function GastronomyModal({
    modalVisibility,
    close,
    item,
    mode = GastronomyModalMode.Edit,
    updateList,
}: IGastronomyModalProps) {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageVisibility, setImageVisibility] = useState(false);

    const { register, form, submit, updateForm, setForm } =
        useForm<IGastronomyForm>({
            url: `/gastronomy/${
                mode === GastronomyModalMode.Edit && item ? item.Id : ''
            }`,
            type: mode === GastronomyModalMode.Edit ? 'PUT' : 'POST',
            onSuccess: () => {
                notification.success({
                    message: t('general.SUCCESS'),
                    description: t(
                        `contentSettingsPage.${
                            mode === GastronomyModalMode.Edit
                                ? 'DISH_EDITED_WITH_SUCCESS'
                                : 'DISH_CREATED_WITH_SUCCESS'
                        }`,
                    ),
                });
                updateList();
                close();
            },
            onError: (data, error: any) => {
                const errorCode = Number(error.errorCode) || 0;
                notification.error({
                    message: t(`general.ERROR`),
                    description: t(`errors.${errorCode}`),
                });
                updateList();
                close();
            },
            resolveData(rawForm) {
                const data = new FormData();
                Object.keys(rawForm).forEach(key => {
                    if (key === 'photo') {
                        rawForm.photo.forEach((f: any) => {
                            data.append('photo', f as any);
                        });
                    } else
                        data.append(
                            key,
                            rawForm[key as keyof IGastronomyForm] as any,
                        );
                });
                return data;
            },
        });

    useEffect(() => {
        if (modalVisibility && mode === GastronomyModalMode.Edit && !item) {
            close();
        }
        if (modalVisibility && mode === GastronomyModalMode.Edit && item) {
            const currentDate = new Date();
            updateForm('name', item.Gastronomylanguages[0].Name);
            updateForm('description', item.Gastronomylanguages[0].Description);
            updateForm('contact', item.Contact);
            setImageUrl(
                `${config.API_URL}/gastronomy/images/${item.Id}?date=${currentDate}`,
            );
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
        if (!modalVisibility) {
            setForm({});
            setImageVisibility(false);
        }
        if (modalVisibility && mode === GastronomyModalMode.Create) {
            setImageUrl(undefined);
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
    }, [modalVisibility]);

    return (
        <Modal
            visible={modalVisibility}
            close={close}
            className='gastronomy-modal'
            width={400}
        >
            <motion.div
                {...contentSettingsModalMotion}
                className='gastronomy-modal__content'
            >
                <p className='gastronomy-modal__content__title'>
                    {t(
                        `contentSettingsPage.${
                            mode === GastronomyModalMode.Edit
                                ? 'EDIT_DISH'
                                : 'CREATE_DISH'
                        }`,
                    )}
                </p>
                <div className='gastronomy-modal__content__body'>
                    <p className='gastronomy-modal__content__body__label'>
                        {t('contentSettingsPage.DISH_NAME')}
                    </p>
                    <Input
                        {...register('name', {
                            required: true,
                            resolveError: () =>
                                `${t('form.NAME')} ${t('form.REQUIRED_FIELD')}`,
                        })}
                        className='gastronomy-modal__content__body__field'
                    />
                    <p className='gastronomy-modal__content__body__label'>
                        {t('form.CONTACT')}
                    </p>
                    <PhoneInput
                        {...register('contact', {
                            required: true,
                            regex: phoneRegex,
                            resolveError: (val, error) => {
                                if (error.type === 'regex') {
                                    return `${t('general.THE')} ${t(
                                        'form.CONTACT',
                                    )} ${t('form.FORMAT_INVALID')}`;
                                }
                                return `${t('form.CONTACT')} ${t(
                                    'form.REQUIRED_FIELD',
                                )}`;
                            },
                        })}
                        className='gastronomy-modal__content__body__field'
                    />
                    <p className='gastronomy-modal__content__body__label'>
                        {t('contentSettingsPage.DISH_DESCRIPTION')}
                    </p>
                    <TextArea
                        {...register('description', {
                            required: true,
                            resolveError: () =>
                                `${t('form.DESCRIPTION')} ${t(
                                    'form.REQUIRED',
                                )}`,
                        })}
                        autoSize
                        className='gastronomy-modal__content__body__field'
                    />
                    {imageVisibility && (
                        <FileUploader
                            {...register('photo')}
                            initial={imageUrl}
                            className='rectangular'
                        />
                    )}
                </div>
                <div className='gastronomy-modal__content__footer'>
                    <SiteButton
                        text={t('general.CANCEL')}
                        type='primary'
                        className='gastronomy-modal__content__footer__button grey'
                        onClick={close}
                    />
                    <SiteButton
                        text={t('form.SAVE_CHANGES')}
                        type='primary'
                        className='gastronomy-modal__content__footer__button'
                        onClick={submit}
                        disabled={
                            mode === GastronomyModalMode.Create &&
                            (!form.name ||
                                !form.description ||
                                !form.contact ||
                                !form.photo ||
                                (form.photo &&
                                    Array.isArray(form.photo) &&
                                    !(form.photo.length > 0)))
                        }
                    />
                </div>
            </motion.div>
        </Modal>
    );
}

export default GastronomyModal;
